import { NextPage } from 'next'
import { Article } from '../../types'
import { fetchArticles, fetchArticlesTotalCount } from '@/lib/article'
import { ArticleCard } from '@/components/pages/articles/ArticleCard'
import { mq, colors, breakpoint } from 'plume-ui'
import { css } from '@emotion/react'
import InfiniteScroll from 'react-infinite-scroller'
import React from 'react'
import { FeatureLabel } from '@/components/pages/articles/FeaturedLabel'
import { Template } from '@/components/common/Template'
import { ArticlesHead } from '@/components/common/Head'
import { Loader } from '@/components/atoms/Loader'
import { FeaturedSlider } from '@/components/pages/articles/FeaturedSlider'

type Props = {
  articles: Article[]
  total: number
}

const ArticlesPage: NextPage<Props> = (props) => {
  const [articles, setArticles] = React.useState<Article[]>(props.articles)
  const [readMore, setReadMore] = React.useState<Boolean>(false)
  if (!articles) {
    return null
  }

  const getArticlesRequest = async () => {
    const additionalArticles = await fetchArticles(articles.length)
    setArticles(articles.concat(additionalArticles))
  }

  const hasMore = articles.length !== 0 && articles.length < props.total

  return (
    <Template className="articles-page">
      <ArticlesHead />
      <div
        css={css`
          background-color: ${colors.gray40};
        `}
      >
        <div
          css={css`
            max-width: ${breakpoint.extraLarge}px;
            margin: 0 auto;
            padding-block: 16px 8px;
            padding-inline: 24px;
            ${mq.large} {
              padding-block: 32px 24px;
              padding-inline: 0;
            }
          `}
        >
          <FeatureLabel>オリジナル記事</FeatureLabel>
        </div>
        <ArticleList articles={articles.slice(0, 6)}></ArticleList>
        <FeaturedSlider />
        <ArticleList
          articles={articles.slice(6, 12)}
          css={css`
            ${mq.large} {
              margin-bottom: 32px;
            }
          `}
        ></ArticleList>
        {readMore ? (
          <InfiniteScroll
            pageStart={0}
            loadMore={getArticlesRequest}
            hasMore={hasMore}
            loader={<PageLoader key={0} />}
          >
            <ArticleList
              articles={articles.slice(12)}
              css={css`
                padding-bottom: 64px;
              `}
            ></ArticleList>
          </InfiniteScroll>
        ) : (
          <div
            css={css`
              padding-bottom: 30px;
            `}
          >
            <ArticleList articles={articles.slice(12)}></ArticleList>
            <div
              css={css`
                display: block;
                max-width: ${breakpoint.extraLarge}px;
                border: 1px solid ${colors.black30};
                border-radius: 2px;
                padding: 18px;
                margin: 20px 24px 64px;
                text-align: center;
                font-size: 14px;
                line-height: 1;
                color: ${colors.black10};
                :hover {
                  color: ${colors.white00};
                  background-color: ${colors.black10};
                  cursor: pointer;
                }
                ${mq.large} {
                  margin: 32px auto 64px;
                }
              `}
              onClick={() => {
                setReadMore(true)
              }}
            >
              もっと見る
            </div>
          </div>
        )}
      </div>
    </Template>
  )
}

const ArticleList: React.FC<{ articles: Article[]; className?: string }> = ({
  articles,
  className,
}) => {
  return (
    <div
      css={css`
        ${mq.large} {
          padding: 0 24px;
        }
      `}
    >
      <ul
        css={css`
          display: grid;
          max-width: ${breakpoint.extraLarge}px;
          margin: 0px auto;
          padding: 0;
          ${mq.large} {
            grid-template-columns: repeat(auto-fill, minmax(352px, 1fr));
            grid-gap: 32px;
          }
        `}
        className={className}
      >
        {articles.map((article) => (
          <ArticleCard
            className="article-card"
            article={article}
            key={article.id}
          />
        ))}
      </ul>
    </div>
  )
}

const PageLoader = () => {
  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        padding-top: 10px;
        padding-bottom: 20px;
      `}
    >
      <Loader />
    </div>
  )
}

ArticlesPage.getInitialProps = async () => {
  const [articles, total] = await Promise.all([
    fetchArticles(),
    fetchArticlesTotalCount(),
  ])
  return {
    articles,
    total,
  }
}

export default ArticlesPage
